import { Link } from "@tanstack/react-router";
import { Badge, Skeleton } from "@/components/kit";
import type { CustomerWatchlistResultDTO } from "@/modules/reports/domain/types";
import { formatLak } from "@/shared/lib/currency";
import { customerRiskTierMeta } from "@/modules/customers/presentation/ui/customer-risk";
import { ReportMobileCard, ReportStat } from "./ReportProductLink";

type CustomerWatchlistTableProps = {
  result?: CustomerWatchlistResultDTO;
  isLoading?: boolean;
};

function formatLastOrderAt(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("lo-LA", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function RiskTierBadge({
  tier,
  score,
}: {
  tier: CustomerWatchlistResultDTO["data"][number]["riskTier"];
  score: number;
}) {
  const meta = customerRiskTierMeta(tier);
  return (
    <Badge variant={meta.variant} className="tabular-nums">
      {meta.shortLabel} · {score}
    </Badge>
  );
}

function WatchlistMobileCard({
  row,
}: {
  row: CustomerWatchlistResultDTO["data"][number];
}) {
  return (
    <ReportMobileCard>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link
            to="/app/customers/$id"
            params={{ id: row.customerId }}
            className="font-medium text-secondary hover:underline"
          >
            {row.name}
          </Link>
          <div className="text-muted-foreground text-xs">
            {[row.phone, row.province, row.district].filter(Boolean).join(" · ") ||
              "—"}
          </div>
        </div>
        <RiskTierBadge tier={row.riskTier} score={row.riskScore} />
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <ReportStat label="COD ຕີກັບ" value={row.codReturnCount} />
        <ReportStat label="ອໍເດີ້" value={row.orderCount} />
        <ReportStat label="ຍອດ" value={formatLak(row.revenue)} />
        <ReportStat
          label="ຊື້ລ່າສຸດ"
          value={formatLastOrderAt(row.lastOrderAt)}
        />
      </dl>
    </ReportMobileCard>
  );
}

export function CustomerWatchlistTable({
  result,
  isLoading,
}: CustomerWatchlistTableProps) {
  const rows = result?.data ?? [];

  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-2xl" />;
  }

  return (
    <section className="brand-card overflow-hidden">
      <div className="flex flex-col gap-1 p-4 sm:p-5">
        <h2 className="font-display text-lg">ລາຍການເຝົາລະວັງ</h2>
        <p className="text-muted-foreground text-sm">
          ລູກຄ້າຄວນເຝົາລະວັງ COD — ຄລິກເພື່ອເບິ່ງໂປຣໄຟລ໌
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="border-secondary/15 border-t p-8 text-center text-muted-foreground text-sm">
          ບໍ່ມີຂໍ້ມູນໃນຊ່ວງນີ້
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3 border-secondary/15 border-t p-4 md:hidden">
            {rows.map((row) => (
              <WatchlistMobileCard key={row.customerId} row={row} />
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs">
                <tr>
                  <th className="px-4 py-3 font-medium">ລູກຄ້າ</th>
                  <th className="px-4 py-3 font-medium">Tier</th>
                  <th className="px-4 py-3 font-medium text-right">
                    COD ຕີກັບ
                  </th>
                  <th className="px-4 py-3 font-medium text-right">ອໍເດີ້</th>
                  <th className="px-4 py-3 font-medium text-right">ຍອດ</th>
                  <th className="px-4 py-3 font-medium">ຊື້ລ່າສຸດ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.customerId}
                    className="border-secondary/10 border-t"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to="/app/customers/$id"
                        params={{ id: row.customerId }}
                        className="font-medium text-secondary hover:underline"
                      >
                        {row.name}
                      </Link>
                      {row.phone ? (
                        <div className="text-muted-foreground text-xs">
                          {row.phone}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-3">
                      <RiskTierBadge tier={row.riskTier} score={row.riskScore} />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.codReturnCount}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.orderCount}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatLak(row.revenue)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground text-xs">
                      {formatLastOrderAt(row.lastOrderAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
